import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  LinearProgress, MenuItem, Pagination, Paper, Stack, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import GridViewIcon from '@mui/icons-material/GridView';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ViewListIcon from '@mui/icons-material/ViewList';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AuthenticatedMedia from '../components/chat/AuthenticatedMedia';
import { attachmentSizeError } from '../components/chat/attachmentLimits';
import {
  bulkDeleteLibraryMedia, deleteLibraryMedia, downloadLibraryMedia, downloadMediaFile, libraryMediaSource, listLibraryMedia, uploadLibraryMedia
} from '../services/mediaLibrary.service';
import { getAccessPayload } from '../utils/access';

export const LIBRARY_ACCEPT = ['.jpg', '.jpeg', '.png', '.webp', '.mp4', '.3gp', '.mp3', '.ogg', '.amr', '.m4a', '.aac', '.opus', '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'];
const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' }, { value: 'image', label: 'Images' }, { value: 'video', label: 'Videos' },
  { value: 'audio', label: 'Audio' }, { value: 'document', label: 'Documents' }
];
// Storage categories. Only the Media Library is managed here; chat and flow
// media are read-only views served by their own access-checked routes.
const CATEGORIES = [
  { value: 'library', label: 'Media Library', permissions: ['flow-builder.view', 'campaigns.view'] },
  { value: 'chat', label: 'Chat media', permissions: ['inbox.view'] },
  { value: 'flow', label: 'Flow media', permissions: ['flow-builder.view'] }
];
const CATEGORY_LABEL = { library: 'Media Library', chat: 'Chat', flow: 'Flow' };
const PAGE_SIZE = 24;
const MAX_BULK_DOWNLOAD = 20;
const MAX_BULK_DELETE = 50;
const can = (access, permissions) => access.isSystemAdmin || permissions.some((permission) => access.permissions?.includes(permission));

export function libraryMediaType(file) {
  const extension = `.${String(file?.name || '').split('.').pop().toLowerCase()}`;
  if (['.jpg', '.jpeg', '.png', '.webp'].includes(extension)) return 'image';
  if (['.mp4', '.3gp'].includes(extension)) return 'video';
  if (['.mp3', '.ogg', '.amr', '.m4a', '.aac', '.opus'].includes(extension)) return 'audio';
  if (['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv'].includes(extension)) return 'document';
  return null;
}

export function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(value / 1024))} KB`;
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const timer = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(timer); }, [value, delay]);
  return debounced;
}

const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^;]+;base64,/, ''));
  reader.onerror = () => reject(new Error('The file could not be read.'));
  reader.readAsDataURL(file);
});
const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;
const itemKey = (item) => item.key || `library:${item.id}`;
const itemSource = (item) => item.downloadUrl || libraryMediaSource(item.id);
const isPdf = (item) => String(item?.mimeType || '').toLowerCase() === 'application/pdf' || /\.pdf$/i.test(item?.originalName || '');
const fetchBlob = (item) => (item.category && item.category !== 'library' ? downloadMediaFile(itemSource(item)) : downloadLibraryMedia(item.id));

function TypeIcon({ mediaType }) {
  if (mediaType === 'audio' || mediaType === 'voice') return <GraphicEqIcon color="action" />;
  return <DescriptionOutlinedIcon color="action" />;
}

function Thumbnail({ item, height = 120 }) {
  const visual = item.mediaType === 'image' || item.mediaType === 'video';
  return (
    <Box sx={{ height, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', overflow: 'hidden', borderRadius: 1 }}>
      {visual
        ? <AuthenticatedMedia source={itemSource(item)} mediaType={item.mediaType} fileName={item.originalName} alt={item.originalName} />
        : <TypeIcon mediaType={item.mediaType} />}
    </Box>
  );
}

// PDF preview from the authenticated blob (never a raw file URL).
function PdfPreview({ item }) {
  const [state, setState] = useState({ url: '', error: '' });
  useEffect(() => {
    let active = true;
    let url = '';
    Promise.resolve()
      .then(() => fetchBlob(item))
      .then((response) => {
        if (!active) return;
        url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        setState({ url, error: '' });
      })
      .catch(() => active && setState({ url: '', error: 'This PDF could not be loaded for preview. Download it instead.' }));
    return () => { active = false; if (url) window.URL.revokeObjectURL(url); };
  }, [item]);
  if (state.error) return <Typography color="text.secondary">{state.error}</Typography>;
  if (!state.url) return <CircularProgress aria-label="Loading preview" />;
  return <Box component="iframe" title="PDF preview" src={state.url} sx={{ width: '100%', height: { xs: 360, md: 520 }, border: 0, borderRadius: 1, bgcolor: 'background.default' }} />;
}

function UsageChip({ item }) {
  if (item.category && item.category !== 'library') {
    return <Tooltip title={item.readOnlyReason || 'Read-only'}><Chip size="small" icon={<LockOutlinedIcon />} label="Read-only" variant="outlined" /></Tooltip>;
  }
  const usage = item.usage;
  if (!usage) return null;
  if (usage.status === 'in_use') return <Tooltip title={`Used by: ${usage.types.join(', ')}`}><Chip size="small" color="warning" variant="outlined" label={`In use · ${usage.count}`} /></Tooltip>;
  if (usage.status === 'unused') return <Chip size="small" variant="outlined" label="Unused" />;
  return <Tooltip title="Usage could not be checked right now. Deleting re-checks every reference."><Chip size="small" variant="outlined" label="Usage unknown" /></Tooltip>;
}

function ItemActions({ item, onPreview, onDownload, onCopy, onDelete, canDelete }) {
  const library = !item.category || item.category === 'library';
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" onClick={(event) => event.stopPropagation()}>
      <Tooltip title="Preview"><IconButton size="small" aria-label={`Preview ${item.originalName}`} onClick={() => onPreview(item)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
      <Tooltip title="Download"><IconButton size="small" aria-label={`Download ${item.originalName}`} onClick={() => onDownload(item)}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
      {library && <Tooltip title="Copy media reference"><IconButton size="small" aria-label={`Copy reference for ${item.originalName}`} onClick={() => onCopy(item)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>}
      {library && canDelete && item.canDelete !== false && <Tooltip title="Delete"><IconButton size="small" color="error" aria-label={`Delete ${item.originalName}`} onClick={() => onDelete(item)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>}
    </Stack>
  );
}

const describeDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

export default function MediaLibraryPage() {
  const access = getAccessPayload();
  const canUpload = can(access, ['flow-builder.edit', 'campaigns.edit']);
  const canDelete = can(access, ['flow-builder.delete', 'campaigns.delete']);
  const categories = CATEGORIES.filter((option) => can(access, option.permissions));
  const [category, setCategory] = useState('library');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);
  const [view, setView] = useState('grid');
  const [selectedId, setSelectedId] = useState(null);
  const [checked, setChecked] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [upload, setUpload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);
  const fileInputRef = useRef(null);
  const uploadingRef = useRef(false);
  const bulkRef = useRef(false);
  const requestRef = useRef(0);
  const debouncedSearch = useDebounced(search);
  const libraryView = category === 'library';

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setLoadError('');
    try {
      const params = { page, limit: PAGE_SIZE, type, search: debouncedSearch };
      const { data: response } = await listLibraryMedia(category === 'library' ? params : { category, ...params });
      if (requestRef.current !== requestId) return; // a newer request (filter/page change) wins
      const result = response.data || { items: [], total: 0, pages: 1 };
      setData(result);
      if (result.items.length === 0 && page > 1 && page > result.pages) setPage(result.pages);
    } catch (error) {
      if (requestRef.current === requestId) setLoadError(errorMessage(error, 'Unable to load the Media Library.'));
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [category, page, type, debouncedSearch]);

  useEffect(() => { load(); }, [load]);
  // Filters always start from the first page so no result is silently skipped.
  useEffect(() => { setPage(1); }, [category, type, debouncedSearch]);
  // A selection never outlives the page / filter it was made on.
  useEffect(() => { setChecked([]); }, [category, page, type, debouncedSearch]);

  // Multiple files are uploaded one at a time through the existing endpoint
  // (content-addressed: re-uploading identical bytes returns the existing item).
  const startUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length || uploadingRef.current) return;
    setActionError('');
    setNotice('');
    setBulkReport(null);
    const rejected = [];
    const accepted = [];
    for (const file of files) {
      const mediaType = libraryMediaType(file);
      if (!mediaType) { rejected.push(`${files.length > 1 ? `${file.name}: ` : ''}Unsupported file type. Use images, MP4/3GP video, audio or PDF/Office/TXT/CSV documents.`); continue; }
      const sizeError = attachmentSizeError(file, mediaType);
      if (sizeError) { rejected.push(`${files.length > 1 ? `${file.name}: ` : ''}${sizeError}`); continue; }
      accepted.push({ file, mediaType });
    }
    if (rejected.length) setActionError(rejected.join(' '));
    if (!accepted.length) { if (fileInputRef.current) fileInputRef.current.value = ''; return; }
    uploadingRef.current = true;
    const outcome = { added: [], duplicate: [], failed: [] };
    try {
      for (const [index, { file, mediaType }] of accepted.entries()) {
        const label = accepted.length > 1 ? `${file.name} (${index + 1} of ${accepted.length})` : file.name;
        setUpload({ name: label, progress: 0 });
        try {
          const dataBase64 = await readAsBase64(file);
          const { data: response } = await uploadLibraryMedia(
            { fileName: file.name, mimeType: file.type || 'application/octet-stream', mediaType, dataBase64 },
            (event) => setUpload({ name: label, progress: event.total ? Math.min(99, Math.round((event.loaded * 100) / event.total)) : 50 })
          );
          (response.duplicate ? outcome.duplicate : outcome.added).push(file.name);
          setSelectedId(response.data?.id ?? null);
        } catch (error) {
          outcome.failed.push({ name: file.name, message: errorMessage(error, 'Upload failed. Nothing was added to the library.') });
        }
      }
    } finally {
      uploadingRef.current = false;
      setUpload(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    if (accepted.length === 1) {
      if (outcome.failed.length) setActionError([...rejected, outcome.failed[0].message].join(' '));
      else setNotice(outcome.duplicate.length ? `"${outcome.duplicate[0]}" is already in the library.` : `"${outcome.added[0]}" was added to the library.`);
    } else {
      const parts = [`${outcome.added.length} added`];
      if (outcome.duplicate.length) parts.push(`${outcome.duplicate.length} already in the library`);
      if (outcome.failed.length) parts.push(`${outcome.failed.length} failed`);
      setNotice(`Upload finished: ${parts.join(', ')}.`);
      if (outcome.failed.length) setActionError([...rejected, ...outcome.failed.map((item) => `${item.name}: ${item.message}`)].join(' '));
    }
    if (outcome.added.length || outcome.duplicate.length) {
      if (category !== 'library') setCategory('library');
      else if (page !== 1) setPage(1); else await load();
    }
  };

  const download = async (item) => {
    setActionError('');
    try {
      const response = await fetchBlob(item);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = item.originalName || 'media';
      link.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      return true;
    } catch (error) {
      setActionError(error?.response?.status === 404 ? 'This file is no longer available.' : error?.response?.status === 403 ? 'You do not have access to this file.' : 'Unable to download this file.');
      return false;
    }
  };

  const copyReference = async (item) => {
    try {
      await navigator.clipboard.writeText(item.reference);
      setNotice(`Copied ${item.reference}`);
    } catch {
      setActionError(`Copy failed. The reference is ${item.reference}`);
    }
  };

  const remove = async () => {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    setActionError('');
    try {
      await deleteLibraryMedia(confirmDelete.id);
      setNotice(`"${confirmDelete.originalName}" was deleted.`);
      if (selectedId === confirmDelete.id) setSelectedId(null);
      setConfirmDelete(null);
      await load();
    } catch (error) {
      const references = error?.response?.data?.details?.references;
      setActionError(errorMessage(error, 'Unable to delete this file.') + (Array.isArray(references) && references.length ? ` (${references.map((ref) => `${ref.type} #${ref.id}`).join(', ')})` : ''));
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // ---- multi-select ---------------------------------------------------------
  const checkedItems = data.items.filter((item) => checked.includes(itemKey(item)));
  const deletableChecked = checkedItems.filter((item) => libraryView && canDelete && item.canDelete !== false);
  const toggleChecked = (item) => setChecked((current) => (current.includes(itemKey(item)) ? current.filter((key) => key !== itemKey(item)) : [...current, itemKey(item)]));
  const allLoadedChecked = data.items.length > 0 && data.items.every((item) => checked.includes(itemKey(item)));

  const bulkDownload = async () => {
    if (bulkRef.current || !checkedItems.length) return;
    bulkRef.current = true;
    setBulkBusy(true);
    setBulkReport(null);
    const targets = checkedItems.slice(0, MAX_BULK_DOWNLOAD);
    let ok = 0;
    try {
      for (const item of targets) if (await download(item)) ok += 1;
    } finally {
      bulkRef.current = false;
      setBulkBusy(false);
    }
    setNotice(`Downloaded ${ok} of ${targets.length} file(s).${checkedItems.length > MAX_BULK_DOWNLOAD ? ` Only the first ${MAX_BULK_DOWNLOAD} selected files are downloaded at a time.` : ''}`);
  };

  const bulkDelete = async () => {
    if (bulkRef.current || !deletableChecked.length) return;
    bulkRef.current = true;
    setBulkBusy(true);
    setActionError('');
    setBulkReport(null);
    const names = new Map(deletableChecked.map((item) => [String(item.id), item.originalName]));
    const results = [];
    try {
      const ids = deletableChecked.map((item) => item.id);
      for (let index = 0; index < ids.length; index += MAX_BULK_DELETE) {
        const { data: response } = await bulkDeleteLibraryMedia(ids.slice(index, index + MAX_BULK_DELETE));
        results.push(...(response.data?.results || []));
      }
    } catch (error) {
      setActionError(errorMessage(error, 'Bulk delete failed. No further files were processed.'));
    } finally {
      bulkRef.current = false;
      setBulkBusy(false);
      setConfirmBulkDelete(false);
    }
    if (results.length) {
      const deleted = results.filter((item) => item.outcome === 'deleted');
      const problems = results.filter((item) => item.outcome !== 'deleted').map((item) => ({
        name: names.get(String(item.id)) || `#${item.id}`,
        reason: item.outcome === 'in_use'
          ? `in use${item.references?.length ? ` (${item.references.map((ref) => `${ref.type} #${ref.id}`).join(', ')})` : ''}`
          : item.outcome === 'not_found' ? 'already removed' : item.message || 'failed'
      }));
      setBulkReport({ deleted: deleted.length, problems });
      setChecked((current) => current.filter((key) => !deleted.some((item) => key === `library:${item.id}`)));
      await load();
    }
  };

  const first = data.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(page * PAGE_SIZE, data.total);
  const itemProps = { onPreview: setPreview, onDownload: download, onCopy: copyReference, onDelete: setConfirmDelete, canDelete };
  const meta = (item) => [item.mediaType, item.mimeType, formatBytes(item.size)].filter(Boolean).join(' · ');
  const select = (item) => (
    <Checkbox size="small" checked={checked.includes(itemKey(item))} onClick={(event) => event.stopPropagation()} onChange={() => toggleChecked(item)}
      inputProps={{ 'aria-label': `Select ${item.originalName}` }} sx={{ p: 0.5 }} />
  );
  const emptyText = debouncedSearch || type !== 'all' ? 'No media matches these filters.'
    : category === 'chat' ? 'No chat media you can access.' : category === 'flow' ? 'No Flow Builder media in flows you can access.' : 'The Media Library is empty.';

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h4" fontWeight={900}>Media Library</Typography>
        <Typography color="text.secondary">Reusable images, videos, audio and documents. Files are private and only open for signed-in users.</Typography>
      </Box>

      {categories.length > 1 && (
        <Tabs value={category} onChange={(_, value) => setCategory(value)} variant="scrollable" allowScrollButtonsMobile aria-label="Media category">
          {categories.map((option) => <Tab key={option.value} value={option.value} label={option.label} />)}
        </Tabs>
      )}
      {!libraryView && <Alert severity="info">{category === 'chat' ? 'Chat media is part of conversation history: you can view and download it, but it cannot be deleted here.' : 'Flow media is managed from the Flow Builder node that uses it. It is shown here read-only.'}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <TextField size="small" fullWidth placeholder="Search by file name" value={search} onChange={(event) => setSearch(event.target.value)} inputProps={{ 'aria-label': 'Search media library' }} />
          <TextField select size="small" value={type} onChange={(event) => setType(event.target.value)} sx={{ minWidth: { md: 160 } }} label="Type">
            {TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, value) => value && setView(value)} aria-label="Library view">
            <ToggleButton value="grid" aria-label="Grid view"><GridViewIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="List view"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
          {canUpload && libraryView && (
            <>
              <Button variant="contained" startIcon={<CloudUploadOutlinedIcon />} disabled={Boolean(upload)} onClick={() => fileInputRef.current?.click()} sx={{ whiteSpace: 'nowrap' }}>Upload</Button>
              <input ref={fileInputRef} type="file" hidden multiple accept={LIBRARY_ACCEPT.join(',')} data-testid="library-file-input" onChange={(event) => startUpload(event.target.files)} />
            </>
          )}
        </Stack>
        {upload && <Box sx={{ mt: 1.5 }}><Typography variant="caption">Uploading {upload.name} — {upload.progress}%</Typography><LinearProgress variant="determinate" value={upload.progress} /></Box>}
      </Paper>

      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
      {actionError && <Alert severity="error" onClose={() => setActionError('')}>{actionError}</Alert>}
      {bulkReport && (
        <Alert severity={bulkReport.problems.length ? 'warning' : 'success'} onClose={() => setBulkReport(null)} data-testid="bulk-report">
          {bulkReport.deleted} file(s) deleted.{bulkReport.problems.length ? ` ${bulkReport.problems.length} not deleted:` : ''}
          {bulkReport.problems.length > 0 && <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{bulkReport.problems.map((item) => <li key={item.name}>{item.name} — {item.reason}</li>)}</Box>}
        </Alert>
      )}

      {data.items.length > 0 && (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="bulk-bar">
          <Button size="small" onClick={() => setChecked(allLoadedChecked ? [] : data.items.map(itemKey))} disabled={bulkBusy}>{allLoadedChecked ? 'Clear selection' : 'Select page'}</Button>
          <Typography variant="body2" color="text.secondary">{checked.length} selected</Typography>
          <Button size="small" startIcon={<DownloadIcon />} disabled={!checkedItems.length || bulkBusy} onClick={bulkDownload}>Download selected</Button>
          {libraryView && canDelete && <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} disabled={!deletableChecked.length || bulkBusy} onClick={() => setConfirmBulkDelete(true)}>Delete selected</Button>}
          {bulkBusy && <CircularProgress size={18} aria-label="Working" />}
        </Stack>
      )}

      {loadError ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{loadError}</Alert>
      ) : loading && !data.items.length ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress aria-label="Loading media" /></Box>
      ) : !data.items.length ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography fontWeight={800}>{emptyText}</Typography>
          {canUpload && libraryView && !debouncedSearch && type === 'all' && <Typography color="text.secondary">Upload a file to start building your library.</Typography>}
        </Paper>
      ) : view === 'grid' ? (
        <Box data-testid="library-grid" sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 1.5 }}>
          {data.items.map((item) => (
            <Paper key={itemKey(item)} data-testid="library-item" variant="outlined" onClick={() => setSelectedId(item.id)} aria-selected={selectedId === item.id}
              sx={{ p: 1, cursor: 'pointer', minWidth: 0, borderColor: selectedId === item.id ? 'primary.main' : undefined, borderWidth: selectedId === item.id ? 2 : 1 }}>
              <Box sx={{ position: 'relative' }}>
                <Thumbnail item={item} />
                <Box sx={{ position: 'absolute', top: 2, left: 2, bgcolor: 'background.paper', borderRadius: 1 }}>{select(item)}</Box>
              </Box>
              <Typography variant="body2" fontWeight={800} noWrap title={item.originalName} sx={{ mt: 1 }}>{item.originalName}</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" label={item.mediaType} />
                <Typography variant="caption" color="text.secondary">{formatBytes(item.size)}</Typography>
                <Typography variant="caption" color="text.secondary">{describeDate(item.createdAt)}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap><UsageChip item={item} /></Stack>
              <ItemActions item={item} {...itemProps} />
            </Paper>
          ))}
        </Box>
      ) : (
        <Stack data-testid="library-list" spacing={1}>
          {data.items.map((item) => (
            <Paper key={itemKey(item)} data-testid="library-item" variant="outlined" onClick={() => setSelectedId(item.id)} aria-selected={selectedId === item.id}
              sx={{ p: 1, cursor: 'pointer', borderColor: selectedId === item.id ? 'primary.main' : undefined, borderWidth: selectedId === item.id ? 2 : 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                {select(item)}
                <Box sx={{ width: { xs: '100%', sm: 72 }, flexShrink: 0 }}><Thumbnail item={item} height={56} /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={800} noWrap title={item.originalName}>{item.originalName}</Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {meta(item)} · {item.category && item.category !== 'library' ? (item.flow?.name || `Conversation #${item.conversationId}`) : (item.uploadedBy?.name || 'Unknown')} · {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                  </Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" variant="outlined" label={CATEGORY_LABEL[item.category || 'library']} />
                    <UsageChip item={item} />
                  </Stack>
                </Box>
                <ItemActions item={item} {...itemProps} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {data.total > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary" data-testid="library-range">Showing {first}–{last} of {data.total}</Typography>
          {data.pages > 1 && <Pagination count={data.pages} page={page} onChange={(_, value) => setPage(value)} size="small" />}
        </Stack>
      )}

      <Dialog open={Boolean(preview)} onClose={() => setPreview(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ wordBreak: 'break-word' }}>{preview?.originalName}</DialogTitle>
        <DialogContent>
          {preview && ['image', 'video', 'audio', 'voice'].includes(preview.mediaType)
            ? <Box sx={{ minHeight: 160, display: 'grid', placeItems: 'center' }}><AuthenticatedMedia source={itemSource(preview)} mediaType={preview.mediaType} fileName={preview.originalName} alt={preview.originalName} /></Box>
            : preview && isPdf(preview)
              ? <PdfPreview item={preview} />
              : <Typography color="text.secondary">Documents are not previewed in the browser. Download the file to open it.</Typography>}
          {preview && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              {preview.mimeType} · {formatBytes(preview.size)}{preview.reference ? ` · Reference ${preview.reference}` : ''}{preview.category && preview.category !== 'library' ? ` · ${CATEGORY_LABEL[preview.category]} (read-only)` : ''}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {preview && <Button onClick={() => download(preview)} startIcon={<DownloadIcon />}>Download</Button>}
          <Button onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onClose={() => !deleting && setConfirmDelete(null)}>
        <DialogTitle>Delete from the Media Library?</DialogTitle>
        <DialogContent>
          <Typography>&quot;{confirmDelete?.originalName}&quot; will be permanently removed. Files still used by a flow, campaign, template, message or scheduled send cannot be deleted.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={remove} disabled={deleting}>{deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmBulkDelete} onClose={() => !bulkBusy && setConfirmBulkDelete(false)}>
        <DialogTitle>Delete {deletableChecked.length} selected file(s)?</DialogTitle>
        <DialogContent>
          <Typography>Only the files you selected on this page are affected. Each file is checked again before deletion; files still in use are skipped and listed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)} disabled={bulkBusy}>Cancel</Button>
          <Button color="error" variant="contained" onClick={bulkDelete} disabled={bulkBusy}>{bulkBusy ? <CircularProgress size={20} color="inherit" /> : 'Delete selected'}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
